import React, { useState, useEffect, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { navigate } from '@reach/router'
import { useTranslation } from 'react-i18next'

import { makeStyles } from '@material-ui/core/styles'
import ArrowForwardIosIcon from '@material-ui/icons/ArrowForwardIos'
import { Grid, CircularProgress, Box, Typography } from '@material-ui/core'

import { PaymentForm } from './PaymentForm'
import { Heading } from '../../components/Text'
import { NavigateBack } from '../../components/Nav/NavigateBack'
import { WhiteButton } from '../../components/Button/WhiteButton'
import { useStoreState, useStoreActions } from '../../store/store.hooks'
import {
  postNewRequest,
  getPaymentStatus,
} from '../../services/request.service'
import { formatPrice, normalizeLanguage } from '../../util'
import { FEATURES, SPECIALIZATIONS } from '../../constants'

const useStyles = makeStyles((theme) => ({
  boxLabel: {
    color: '#6473AF',
    fontWeight: 600,
    fontSize: 15,
    textTransform: 'uppercase',
  },
  originalPrice: {
    fontSize: 20,
    fontWeight: 'bold',
  },
  discountedPrice: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#97D344',
  },
  oldPrice: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#F01E5A',
    textDecoration: 'line-through',
    display: 'inline-block',
    marginRight: 20,
  },
  payment: {
    backgroundColor: '#fff',
    borderRadius: '15px',
  },
  headerContainer: {
    padding: '40px',
    display: 'flex',
    justifyContent: 'center',
    flexDirection: 'column',
    textAlign: 'center',
    [theme.breakpoints.down('xs')]: {
      padding: '20px',
    },
  },
  complaintsFooter: {
    height: '150px',
    borderRadius: ' 0 0 15px 15px;',
    position: 'relative',
    background: 'linear-gradient(180deg, #EFF9FF 0%, rgba(239,249,255,0) 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',

    [theme.breakpoints.down('sm')]: {
      dispaly: 'felx',
      flexDirection: 'column',
    },

    [theme.breakpoints.down('xs')]: {
      height: 120,
    },
  },
  complaintsFooterButton: {
    width: 400,
    boxShadow: ' 0 10px 20px 0 rgba(100,115,175,0.4)',
    border: '2px solid #6473AF;',
    marginLeft: '3rem',

    [theme.breakpoints.down('sm')]: {
      marginBottom: '10px',
    },

    [theme.breakpoints.down('xs')]: {
      marginLeft: 0,
      width: 280,
    },
  },
  loaderIcon: {
    margin: 5,
  },
  pendingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 40px 60px',
    textAlign: 'center',

    [theme.breakpoints.down('xs')]: {
      padding: '0 20px 40px',
    },
  },
  pendingDescription: {
    color: '#6473AF',
    fontSize: 16,
    marginTop: 30,
    maxWidth: 450,
  },
  buttonContent: {
    display: 'flex',
    justifyContent: 'center',
  },
  priceContainer: {
    position: 'absolute',
    textAlign: 'center',
    left: '10%',

    [theme.breakpoints.down('sm')]: {
      marginBottom: '10px',
      position: 'relative',
      left: 0,
    },
  },
}))

//
// Credit card data
//

// TODO implement credit card data fetch
// TODO create card data type
const creditCardMocks = [
  {
    id: 101,
    lastFourDigits: '0003',
    cardName: 'OTP private card',
  },
  {
    id: 102,
    lastFourDigits: '4741',
    cardName: 'K&H private card',
  },
]

// TODO fix after using real data
const getCardIdByIndex = (cardIndex: number) => {
  return creditCardMocks && creditCardMocks.length > 0
    ? creditCardMocks[cardIndex].id
    : -1
}

// TODO implement credit card edit function (fetch and redirect?)
const editCardData = (cardIndex: number) => {
  const cardId = getCardIdByIndex(cardIndex)
  console.log('cardIndex: ' + cardIndex, 'cardId: ' + cardId)
}

//
// Billing address data
//

// TODO implement billing address data fetch
// TODO create billing address type
const billingAddressMocks = [
  {
    id: 201,
    name: 'Otthon',
    fullAddress: '1234 Budapest, Andrássy Út 69.',
  },
  {
    id: 202,
    name: 'Melókahely',
    fullAddress: '1234 Budapest, Homeoffice utca 5.',
  },
]

const editBillingAddress = (addressIndex: number, addresses: any) => {
  const selectedAddress = addresses[addressIndex]
  navigate('/patient/billing-address', { state: { address: selectedAddress } })
}

type FormData = {
  tosCheckedPayment: boolean
}

// gateway can return before the final status is known, IPN settles it later
type PendingPayment =
  | { kind: 'request'; requestId: number } // poll the payment-status endpoint
  | { kind: 'card'; prevCcCount: number } // no request row yet, poll the card list
  | { kind: 'unknown' } // nothing pollable, wait out the timeout

const PENDING_POLL_INTERVAL_MS = 3000
const PENDING_POLL_TIMEOUT_MS = 150000 // hard stop, the IPN can take minutes

// gateway declines a paid charge without Order.BillingData.Phone
const MIN_PHONE_DIGITS = 7
const MAX_PHONE_DIGITS = 18 // gateway cap on digits
const MAX_PHONE_LENGTH = 32 // backend schema caps the raw string
const isValidPhoneNumber = (value: string) => {
  const phoneNumber = value || ''
  const digits = phoneNumber.replace(/\D/g, '')
  return (
    phoneNumber.length <= MAX_PHONE_LENGTH &&
    digits.length >= MIN_PHONE_DIGITS &&
    digits.length <= MAX_PHONE_DIGITS
  )
}

const Payment = () => {
  const { i18n, t } = useTranslation('payment')
  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<FormData>()

  const classes = useStyles()
  const [isLoading, setIsLoading] = useState(false)

  const initialCardIndex =
    creditCardMocks && creditCardMocks.length > 0 ? 0 : -1
  const [selectedCardIndex, setSelectedCardIndex] = useState(initialCardIndex)

  const initialAddressIndex =
    billingAddressMocks && billingAddressMocks.length > 0 ? 0 : -1
  const [selectedAddressIndex, setSelectedAddressIndex] =
    useState(initialAddressIndex)

  /* store state */
  const termsAndConditionsAccepted = useStoreState(
    (state) => state.request.termsAndConditionsAccepted
  )
  const payedAsOneOff = useStoreState((state) => state.request.payedAsOneOff)
  const discountedPrice = useStoreState((state) => state.coupon.discountedPrice)
  const couponCode = useStoreState((state) => state.coupon.couponCode)
  const cards = useStoreState((state) => state.profile.cards)
  const addresses = useStoreState(
    (state) => state.profile.profileContents.addresses
  )
  const profilePhoneNumber = useStoreState(
    (state) => state.profile.profileContents.phoneNumber
  )
  const specialization = useStoreState((state) => state.request.specialization)
  const {
    subscription: { featuresBySku, planCode },
  } = useStoreState((state) => state)

  const language = normalizeLanguage(i18n.language)
  const feature =
    specialization === SPECIALIZATIONS.PEDIATRICS
      ? FEATURES.ON_DEMAND_PEDIATRICIAN
      : language === 'hu'
      ? FEATURES.ON_DEMAND_MEDICAL_DOCTOR
      : FEATURES.ON_DEMAND_MEDICAL_DOCTOR_INTL
  const featurePrice = feature && featuresBySku && featuresBySku[feature]
  const formattedPrice = featurePrice && featurePrice.formattedPrice

  /* store actions */
  const setNotification = useStoreActions(
    (actions) => actions.layout.setNotification
  )
  const layoutSetIsLoading = useStoreActions(
    (actions) => actions.layout.setIsLoading
  )
  const setRequestId = useStoreActions(
    (actions) => actions.request.setRequestId
  )
  const setPayedAsOneOff = useStoreActions(
    (actions) => actions.request.setPayedAsOneOff
  )
  const setOneOffFlowUsed = useStoreActions(
    (actions) => actions.request.setOneOffFlowUsed
  )
  const fetchCard = useStoreActions((actions) => actions.profile.fetchCard)
  const setCouponCode = useStoreActions(
    (actions) => actions.coupon.setCouponCode
  )
  const setDiscountedPrice = useStoreActions(
    (actions) => actions.coupon.setDiscountedPrice
  )
  const { fetchProfile } = useStoreActions((actions) => actions.profile)

  const [hasFullDiscount, updateHasFullDiscount] = useState(false)
  const [isVoucherValidating, setIsVoucherValidating] = useState(false)

  const [phoneNumber, setPhoneNumber] = useState<string>('')
  const [phoneNumberError, setPhoneNumberError] = useState<string>('')
  const phoneNumberTouched = useRef(false)

  const [pendingPayment, setPendingPayment] = useState<PendingPayment | null>(
    null
  )
  const [pendingTimedOut, setPendingTimedOut] = useState(false)

  const paymentSuccess = () => {
    const dataLayer = (window as any).dataLayer || []
    dataLayer.push({
      event: 'Fizetés',
      eventCategory: 'Fizetés',
      eventAction: 'Sikeres fizetés',
      eventLabel: 'Button',
    })

    navigate('/patient/successful-payment')
  }

  const paymentError = () => {
    setNotification({
      isOpen: true,
      type: 'error',
      title: t('error1Title'),
      description: t<string>('error1Description'),
    })
  }

  useEffect(() => {
    setPayedAsOneOff(false)
  }, [])

  useEffect(() => {
    fetchProfile({})
  }, [])

  // must run before the effects below strip the query string
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search)
    // the backend can emit an empty requestId value
    const requestId =
      Number(searchParams.get('requestId')) ||
      Number(localStorage.getItem('requestId'))

    if (searchParams.get('oneOffPaymentPending') === 'true') {
      setOneOffFlowUsed(true)
      if (requestId) {
        setRequestId(requestId)
        localStorage.setItem('requestId', requestId.toString())
        setPendingPayment({ kind: 'request', requestId })
      } else {
        setPendingPayment({ kind: 'unknown' }) // no id to poll with
      }
    } else if (searchParams.get('Success') === 'pending') {
      // no request row yet, the card showing up is the only observable outcome
      setPendingPayment({
        kind: 'card',
        prevCcCount: Number(searchParams.get('prevCcCount')) || 0,
      })
    } else if (searchParams.get('Success') === 'false') {
      paymentError() // declined card registration, handled nowhere else
    }
  }, [])

  // poll until settled or timed out
  useEffect(() => {
    if (!pendingPayment) return

    layoutSetIsLoading(false)

    let finished = false
    const interval = setInterval(async () => {
      if (pendingPayment.kind === 'unknown') {
        return
      }
      if (pendingPayment.kind === 'card') {
        fetchCard({}) // watcher effect below clears the pending state
        return
      }

      try {
        const { status } = await getPaymentStatus(pendingPayment.requestId)
        if (finished) return

        if (status === 'approved') {
          finished = true
          paymentSuccess()
        } else if (status === 'declined') {
          finished = true
          localStorage.removeItem('requestId') // dead id, later flows must not reuse it
          setPendingPayment(null)
          paymentError()
        }
        // 'pending' → keep polling
      } catch {
        // transient error, keep polling
      }
    }, PENDING_POLL_INTERVAL_MS)

    const timeout = setTimeout(() => {
      finished = true
      clearInterval(interval)
      if (pendingPayment.kind === 'request') {
        localStorage.removeItem('requestId') // dead id, later flows must not reuse it
      }
      setPendingTimedOut(true)
    }, PENDING_POLL_TIMEOUT_MS)

    return () => {
      finished = true
      clearInterval(interval)
      clearTimeout(timeout)
    }
  }, [pendingPayment])

  // the card appearing in the list is the success signal for card registration
  useEffect(() => {
    if (
      pendingPayment?.kind === 'card' &&
      cards.length > pendingPayment.prevCcCount
    ) {
      setPendingPayment(null)
      setPendingTimedOut(false)
    }
  }, [cards, pendingPayment])

  // Since Simplepay / paymentgateway.hu is slow we need to handle if the user added the card but it's not showing yet for her
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search)
    // "Success" must be capital case, otherwise it doesn't work with the mobile app
    const success = searchParams.get('Success')
    const oneOffPaymentSuccess = searchParams.get('oneOffPaymentSuccess')
    const prevCcCount = Number(searchParams.get('prevCcCount'))

    const isCardAdded = success === 'true' && prevCcCount === cards.length

    if (isCardAdded) {
      layoutSetIsLoading(true)
      const timeout = setTimeout(() => fetchCard({}), 2000)
      return () => {
        clearTimeout(timeout)
      }
    } else {
      layoutSetIsLoading(false)
      if (!oneOffPaymentSuccess) {
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname
        )
      }
    }
  })

  useEffect(() => {
    const successfulPayment = window.location.search.includes(
      'oneOffPaymentSuccess=true'
    )
    const unsuccessfulPayment = window.location.search.includes(
      'oneOffPaymentSuccess=false'
    )
    if (successfulPayment) {
      setOneOffFlowUsed(true)
      layoutSetIsLoading(true)
      paymentSuccess()
    } else if (unsuccessfulPayment) {
      navigate('/patient/payment')
      paymentError()
    }
  })

  useEffect(() => {
    setCouponCode(undefined)
    setDiscountedPrice(undefined)
  }, [setCouponCode, setDiscountedPrice])

  useEffect(() => {
    // If we have a valid coupon code applied, which guarantess a full 100%
    // discount, don't require a credit card or billing address validation

    const fullDiscountApplied = !!couponCode && discountedPrice === 0
    updateHasFullDiscount(fullDiscountApplied)
  }, [couponCode, discountedPrice])

  useEffect(() => {
    if (hasFullDiscount === true) {
      setPayedAsOneOff(false)
    }
  }, [hasFullDiscount, setPayedAsOneOff])

  useEffect(() => {
    // profile fetch can resolve after the user started typing
    if (profilePhoneNumber && !phoneNumberTouched.current) {
      setPhoneNumber(profilePhoneNumber)
    }
  }, [profilePhoneNumber])

  const onSubmit = handleSubmit(async () => {
    if (!termsAndConditionsAccepted) return
    if (isLoading) return
    setIsLoading(true)

    // full discount never reaches the gateway, no phone needed
    if (!hasFullDiscount && !isValidPhoneNumber(phoneNumber)) {
      setPhoneNumberError(t<string>('error5Description'))
      setNotification({
        isOpen: true,
        type: 'error',
        title: t('error5Title'),
        description: t<string>('error5Description'),
      })
      setIsLoading(false)
      return
    }

    if (!hasFullDiscount && addresses?.length === 0) {
      setNotification({
        isOpen: true,
        type: 'error',
        title: t('error2Title'),
        description: t<string>('error2Description'),
      })
      setIsLoading(false)
      return
    }
    try {
      const selectedCard = cards[selectedCardIndex]

      // Without a full discount, a one-click payment requires a saved card with
      // a referenceTransactionId. If none is available, fall back to the one-off
      // flow (card registration) instead of calling /user/request with an empty
      // referenceTransactionId, which the payment gateway rejects.
      const hasUsableCard = !!selectedCard?.referenceTransactionId
      const effectiveOneOff = hasFullDiscount
        ? false
        : payedAsOneOff || !hasUsableCard

      const request = await postNewRequest({
        payedAsOneOff: effectiveOneOff,
        language,
        paymentDeviceReferenceTransactionId:
          selectedCard?.referenceTransactionId,
        returnTo: 'payment',
        phoneNumber,
        planCode,
      })

      setOneOffFlowUsed(effectiveOneOff)
      setRequestId(request.requestId)
      localStorage.setItem('requestId', request.requestId.toString())

      if (request.paymentStatus === 'pending') {
        // a resumed one-off comes back 202 pending with no url to open
        setIsLoading(false)
        setPendingPayment({ kind: 'request', requestId: request.requestId })
      } else if (effectiveOneOff) {
        navigate(request.url)
      } else {
        paymentSuccess()
      }
    } catch (error: any) {
      // server gates the phone too, a card-flavoured toast would be unactionable
      const missingPhone = error?.code === 'PHONE_NUMBER_REQUIRED'
      if (missingPhone) {
        setPhoneNumberError(t<string>('error5Description'))
      }
      const title = missingPhone
        ? t('error5Title')
        : !cards[0]
        ? t('error4Title')
        : t('error3Title')
      const description = missingPhone
        ? t<string>('error5Description')
        : !cards[0]
        ? t('error3Description')
        : t('error1Description')

      setIsLoading(false)
      setNotification({
        isOpen: true,
        type: 'error',
        title: title,
        description: description,
      })
    }
  })

  const validateForm = () => {
    if (!termsAndConditionsAccepted) {
      setNotification({
        isOpen: true,
        type: 'error',
        title: t('error4Title'),
        description: t<string>('error4Description'),
      })
    }
  }

  const handleVoucherValidating = (isVoucherValidating: boolean) => {
    setIsVoucherValidating(isVoucherValidating)
  }

  const handleSetPhoneNumber = (phoneNumber: string) => {
    phoneNumberTouched.current = true
    setPhoneNumber(phoneNumber)
    if (phoneNumberError) {
      setPhoneNumberError('')
    }
  }

  const creditCards = cards?.map((card) => {
    return {
      id: card.cardReferenceId,
      lastFourDigits: card.paymentDeviceNumber,
      cardName: card.cardType,
      referenceTransactionId: card.referenceTransactionId,
    }
  })

  const billingAddresses = addresses?.map((address) => {
    return {
      id: address.id,
      name: address.userGivenName,
      fullAddress: `${address.postalCode} ${address.city} ${address.streetAddress}`,
    }
  })

  const getPriceContent = () => {
    const formattedDiscountedPrice = formatPrice(
      discountedPrice,
      featurePrice?.currency
    )

    if (discountedPrice || discountedPrice === 0) {
      return (
        <Box display="flex">
          <Typography className={classes.oldPrice}>{formattedPrice}</Typography>
          <Typography className={classes.discountedPrice}>
            {formattedDiscountedPrice}
          </Typography>
        </Box>
      )
    }

    return (
      <Box>
        <Typography className={classes.originalPrice}>
          {formattedPrice}
        </Typography>
      </Box>
    )
  }

  if (pendingPayment) {
    return (
      <div className={classes.payment}>
        {pendingTimedOut && (
          // escape hatch once polling gave up
          <NavigateBack
            onBack={() => {
              setPendingPayment(null)
              setPendingTimedOut(false)
            }}
          />
        )}
        <Grid item xs={12} className={classes.headerContainer}>
          <Heading>
            {t<string>(
              pendingTimedOut ? 'pendingTimeoutTitle' : 'pendingTitle'
            )}
          </Heading>
        </Grid>
        <Box className={classes.pendingContainer}>
          {pendingTimedOut ? (
            <Typography className={classes.pendingDescription}>
              {t<string>('pendingTimeoutDescription')}
            </Typography>
          ) : (
            <>
              <CircularProgress size={48} />
              <Typography className={classes.pendingDescription}>
                {t<string>('pendingDescription')}
              </Typography>
            </>
          )}
        </Box>
      </div>
    )
  }

  return (
    <div className={classes.payment}>
      <NavigateBack />
      <Grid item xs={12} className={classes.headerContainer}>
        <Heading enableTrustLogo>{t<string>('heading')}</Heading>
      </Grid>
      <form onSubmit={onSubmit}>
        <PaymentForm
          control={control}
          creditCardList={creditCards}
          billingAddressList={billingAddresses}
          selectedCardIndex={selectedCardIndex}
          setSelectedCardIndex={setSelectedCardIndex}
          selectedAddressIndex={selectedAddressIndex}
          setSelectedAddressIndex={setSelectedAddressIndex}
          editBillingAddress={(addressIndex: number) =>
            editBillingAddress(addressIndex, addresses)
          }
          editCardData={(cardIndex: number) => editCardData(cardIndex)}
          disableBillingAddress={hasFullDiscount}
          disableCreditCards={hasFullDiscount}
          onVoucherValidating={handleVoucherValidating}
          setPhoneNumber={handleSetPhoneNumber}
          phoneNumber={phoneNumber}
          phoneNumberRequired={!hasFullDiscount}
          phoneNumberError={phoneNumberError}
        />
        <Grid xs={12} container className={classes.complaintsFooter}>
          <Box className={classes.priceContainer}>
            <Typography className={classes.boxLabel}>
              {t<string>('payment')}
            </Typography>
            {getPriceContent()}
          </Box>

          <WhiteButton
            type={'submit'}
            className={classes.complaintsFooterButton}
            onClick={() => validateForm()}
            disabled={isVoucherValidating}
          >
            {isLoading ? (
              <CircularProgress
                className={classes.loaderIcon}
                color="inherit"
                size={24}
              />
            ) : (
              <div className={classes.buttonContent}>
                {t<string>('buttonText')}
                <ArrowForwardIosIcon />
              </div>
            )}
          </WhiteButton>
        </Grid>
      </form>
    </div>
  )
}

export { Payment }
